import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { useGetIdentity } from 'react-admin';
import { setAmbientActiveRegion } from './activeRegion';
import {
  accessibleRegions as deriveAccessibleRegions,
  toRegion,
  type AccessibleRegion,
  type Region,
} from './permissions';

/**
 * When a GLOBAL user has not chosen a region (the switcher UI is deferred to
 * 0.5), default the active region to UK. Documented default so behaviour is
 * predictable until the switcher lands.
 */
export const GLOBAL_DEFAULT_ACTIVE_REGION: AccessibleRegion = 'UK';

/** Region scope used when the token has no / an invalid region (most-restrictive). */
const FALLBACK_REGION: Region = 'UK';

export interface RegionContextValue {
  /** The user's region scope from the token (UK | US | GLOBAL). */
  readonly region: Region;
  /** Concrete regions the user can see (UK→[UK], US→[US], GLOBAL→[UK,US]). */
  readonly accessibleRegions: AccessibleRegion[];
  /** The currently-active region (single-region users: their region). */
  readonly activeRegion: AccessibleRegion;
  /** Setter for the 0.5 switcher to change the active region. */
  readonly setActiveRegion: (region: AccessibleRegion) => void;
  readonly isLoading: boolean;
}

const RegionContext = createContext<RegionContextValue | undefined>(undefined);

/**
 * Provides region scope derived from the authenticated user's `custom:region`.
 * Exposes `accessibleRegions`, an `activeRegion` (with a sensible default) and a
 * setter so the 0.5 switcher can wire in without refactoring. No switcher UI is
 * built here.
 */
export const RegionProvider = ({ children }: { children: ReactNode }) => {
  const { identity, isLoading } = useGetIdentity();
  const region =
    toRegion((identity as { region?: unknown } | undefined)?.region) ??
    FALLBACK_REGION;

  const accessible = useMemo(() => deriveAccessibleRegions(region), [region]);

  // GLOBAL users default to UK; single-region users to their only region.
  const defaultActive: AccessibleRegion = accessible.includes(
    GLOBAL_DEFAULT_ACTIVE_REGION,
  )
    ? GLOBAL_DEFAULT_ACTIVE_REGION
    : accessible[0];

  const [override, setOverride] = useState<AccessibleRegion | null>(null);
  const activeRegion =
    override && accessible.includes(override) ? override : defaultActive;

  // Keep the ambient holder in sync so the (outside-React) audit wrapper can
  // stamp the active region onto entries. See ./activeRegion.ts.
  useEffect(() => {
    setAmbientActiveRegion(activeRegion);
  }, [activeRegion]);

  const value = useMemo<RegionContextValue>(
    () => ({
      region,
      accessibleRegions: accessible,
      activeRegion,
      setActiveRegion: setOverride,
      isLoading,
    }),
    [region, accessible, activeRegion, isLoading],
  );

  return (
    <RegionContext.Provider value={value}>{children}</RegionContext.Provider>
  );
};

/** Access the region context. Must be used within a <RegionProvider>. */
export function useRegion(): RegionContextValue {
  const ctx = useContext(RegionContext);
  if (!ctx) {
    throw new Error('useRegion must be used within a RegionProvider');
  }
  return ctx;
}
